'use client'

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { MdAdd, MdMonitorHeart, MdSearch, MdClose } from 'react-icons/md'
import { getVitalSigns, VitalSign } from '@/lib/api/hospital'
import { HospitalSelector } from '@/components/HospitalSelector'
import { useHospitals } from '@/hooks/useHospitals'

export default function VitalSignsPage() {
    const navigate = useNavigate()
    const [vitalSigns, setVitalSigns] = useState<VitalSign[]>([])
    const [filteredVitalSigns, setFilteredVitalSigns] = useState<VitalSign[]>(
        []
    )
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const {
        hospitals,
        loading: loadingHospitals,
        selectedHospitalId,
        setSelected,
    } = useHospitals()

    useEffect(() => {
        loadVitalSigns()
    }, [selectedHospitalId])

    useEffect(() => {
        if (searchTerm.trim()) {
            const filtered = vitalSigns.filter(vs =>
                vs.admission?.client.name
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase())
            )
            setFilteredVitalSigns(filtered)
        } else {
            setFilteredVitalSigns(vitalSigns)
        }
    }, [searchTerm, vitalSigns])

    const loadVitalSigns = async () => {
        setLoading(true)
        try {
            const response = await getVitalSigns(undefined, {
                hospitalId: selectedHospitalId || undefined,
            })
            if (response.success) {
                setVitalSigns(response.result)
                setFilteredVitalSigns(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar sinais vitais')
            }
        } catch (error) {
            toast.error('Erro ao carregar sinais vitais')
        } finally {
            setLoading(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando sinais vitais...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-pink-600 flex items-center justify-center shadow-lg">
                        <MdMonitorHeart className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Sinais Vitais
                        </h1>
                        <p className="text-sm text-base-content/60">
                            Registros de sinais vitais dos pacientes
                        </p>
                    </div>
                </div>
                <button
                    className="btn btn-secondary gap-2"
                    onClick={() => navigate('/hospital/vital-signs/new')}
                >
                    <MdAdd className="text-xl" />
                    <span className="hidden sm:inline">Registrar</span>
                </button>
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4 flex flex-col gap-3">
                <HospitalSelector
                    hospitals={hospitals}
                    selectedId={selectedHospitalId}
                    onChange={setSelected}
                    loading={loadingHospitals}
                />
                <div className="flex items-center gap-2">
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por paciente..."
                            className="input input-bordered w-full pl-10"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                </div>
            </div>

            {filteredVitalSigns.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdMonitorHeart className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            Nenhum registro encontrado
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            Comece registrando sinais vitais dos pacientes
                        </p>
                        <button
                            onClick={() =>
                                navigate('/hospital/vital-signs/new')
                            }
                            className="btn btn-secondary gap-2"
                        >
                            <MdAdd className="text-xl" />
                            Registrar sinais vitais
                        </button>
                    </div>
                </div>
            ) : (
                <div className="overflow-x-auto bg-base-100 rounded-xl border border-base-300/50">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Paciente</th>
                                <th>Leito</th>
                                <th>Temp</th>
                                <th>FC</th>
                                <th>FR</th>
                                <th>PA</th>
                                <th>SpO2</th>
                                <th>Data/Hora</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredVitalSigns.map(vs => (
                                <tr
                                    key={vs.id}
                                    className="hover cursor-pointer"
                                    onClick={() =>
                                        navigate(
                                            `/hospital/vital-signs/${vs.id}`
                                        )
                                    }
                                >
                                    <td>
                                        <div className="font-bold">
                                            {vs.admission?.client.name}
                                        </div>
                                    </td>
                                    <td>{vs.admission?.bed?.number || '-'}</td>
                                    <td>
                                        {vs.temperature
                                            ? `${vs.temperature}C`
                                            : '-'}
                                    </td>
                                    <td>
                                        {vs.heartRate
                                            ? `${vs.heartRate}bpm`
                                            : '-'}
                                    </td>
                                    <td>
                                        {vs.respiratoryRate
                                            ? `${vs.respiratoryRate}rpm`
                                            : '-'}
                                    </td>
                                    <td>
                                        {vs.systolicBP && vs.diastolicBP
                                            ? `${vs.systolicBP}x${vs.diastolicBP}`
                                            : '-'}
                                    </td>
                                    <td>
                                        {vs.oxygenSaturation
                                            ? `${vs.oxygenSaturation}%`
                                            : '-'}
                                    </td>
                                    <td>
                                        {new Date(vs.measuredAt).toLocaleString(
                                            'pt-BR'
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}
